import { FormEvent, ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { StaffContext, useStaffGate } from '../../lib/staffContext';
import { StaffShell } from '../../ui/StaffShell';
import { ContextError } from '../../ui/ContextError';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { AuditList } from '../admin/AuditList';
import { ChecklistItem, errorText, fmtDate, rpc } from '../admin/api';
import { checklistLabel } from '../admin/checklist';
import { headNav } from './headNav';
import { useSchoolDates } from './useSchoolDates';
import { langList, ownerLabel } from '../teacher/readiness';
import { PaperworkPill, StatePill, TypePill } from '../admin/pills';
import { HeadApprovals } from '../HeadApprovals';
import { HeadInsights } from './HeadInsights';

// The head of school's own screens (spec Workflow H, 0015):
//   /school           Overview — who you are here, Approvals (primary or
//                     delegate), Deputies and the approval delegate (primary),
//                     and the school's status, read-only.
//   /school/results   the school-wide total (whole-school heads only)
//   /school/activity  the school's audit log
// Heads see anonymous patterns and decide who gets access. They never
// provision accounts or classes (that's the school admin's work), and they
// never see a single class's results.

export type Deputy = {
  user_id: string;
  name: string;
  is_test: boolean;
  scope_grade_ids: string[] | null;
  scope_grades: string[] | null;
  is_delegate: boolean;
};

export type HeadHomeData = {
  me: { is_primary: boolean; is_delegate: boolean; delegate_ends_on: string | null; scope_grades: string[] | null };
  school: {
    id: string;
    name: string;
    code: string;
    school_type: 'sandbox' | 'customer';
    state: 'setting_up' | 'live' | 'suspended' | 'offboarded';
    paperwork_status: 'not_started' | 'sent' | 'signed';
    paperwork_signed_on: string | null;
  };
  checklist: ChecklistItem[];
  school_admins: string[];
  primary_head: string | null;
  pending_approvals: number | null;
  classes_missing_size: number;
  delegate: { user_id: string; name: string; ends_on: string | null; since: string } | null;
  deputies: Deputy[] | null;
  grades: { id: string; label: string; level: string }[] | null;
};

export type Tab = 'overview' | 'results' | 'activity' | 'report' | 'council' | 'playbook' | 'compliance' | 'settings' | 'questions' | 'whatworks' | 'staff' | 'calendar';

export function HeadFrame({ tab, children, title: titleOverride }: { tab: Tab; title?: string; children: (ctx: StaffContext, home: HeadHomeData, reload: () => Promise<void>) => ReactNode }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { ctx, error: ctxError } = useStaffGate(['head_of_school']);
  const [home, setHome] = useState<HeadHomeData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const r = await rpc<HeadHomeData>('head_home');
    if (r.error) setError(errorText(t, r.error));
    else setHome(r.data);
  }, [t]);

  useEffect(() => {
    if (ctx) reload();
  }, [ctx, reload]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/teacher/login');
  }

  if (ctxError) return <ContextError message={ctxError} />;
  if (!ctx) return <main className="page-loading" role="status">{t.loading}</main>;

  const nav = headNav(t, tab);
  const title = titleOverride ?? (tab === 'overview' ? t['hs.title'] : tab === 'results' ? t['school.title'] : t['hs.activityTitle']);

  return (
    <StaffShell role={t['role.headOfSchool']} nav={nav} title={title} who={ctx.display_name} ctx={ctx} onSignOut={signOut}>
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : !home ? (
        <p role="status" className="text-muted">
          {t.loading}
        </p>
      ) : (
        children(ctx, home, reload)
      )}
    </StaffShell>
  );
}

export function HeadHome() {
  return <HeadFrame tab="overview">{(_ctx, home, reload) => <Overview home={home} reload={reload} />}</HeadFrame>;
}

export function HeadResults() {
  return <HeadFrame tab="results">{() => <HeadInsights />}</HeadFrame>;
}

export function HeadActivity() {
  const { t } = useI18n();
  return (
    <HeadFrame tab="activity">
      {() => <AuditList scope="school" source="school" title={t['hs.activityTitle']} intro={t['hs.activityIntro']} />}
    </HeadFrame>
  );
}

function Overview({ home, reload }: { home: HeadHomeData; reload: () => Promise<void> }) {
  const { t, locale } = useI18n();
  const { me } = home;
  const approver = me.is_primary || me.is_delegate;

  return (
    <>
      <section className="panel p5" aria-labelledby="hs-who">
        <h2 className="panel-title" id="hs-who">
          {home.school.name}
        </h2>
        <p>
          {me.is_primary
            ? t['hs.youArePrimary']
            : home.primary_head
              ? t['hs.youAreDeputy'](home.primary_head)
              : t['hs.youAreDeputyNoPrimary']}
        </p>
        <p>{me.scope_grades ? t['hs.scopeGrades'](me.scope_grades.join(', ')) : t['hs.scopeWhole']}</p>
        {me.is_delegate && (
          <p className="notice">
            {me.delegate_ends_on ? t['hs.delegateYouUntil'](fmtDate(locale, me.delegate_ends_on)) : t['hs.delegateYou']}
          </p>
        )}
      </section>

      <div className="two-col">
        <div className="stack">
          {approver && <HeadApprovals asDelegate={!me.is_primary} onChanged={reload} />}
          {me.is_primary && home.deputies && <Deputies home={home} reload={reload} />}
        </div>
        <SchoolStatus home={home} />
      </div>
    </>
  );
}

function SchoolStatus({ home }: { home: HeadHomeData }) {
  const { t } = useI18n();
  const facts = useSchoolDates();
  const s = home.school;
  const sandbox = s.school_type === 'sandbox';
  return (
    <section className="panel p5" aria-labelledby="hs-status">
      <h2 className="panel-title" id="hs-status">
        {t['hs.statusTitle']}
      </h2>
      <p className="form-hint">
        {sandbox
          ? t['r4.head.statusSandbox']
          : home.school_admins.length > 0
            ? t['hs.statusIntro'](home.school_admins.join(', '))
            : t['hs.statusIntroNoAdmin']}
      </p>
      {s.state === 'setting_up' && home.checklist.some((c) => c.level === 'required' && !c.done) && (
        <p className="notice notice-block">{t['r4.head.teachersBlocked']}</p>
      )}
      <dl className="kv">
        <div>
          <dt>{t['hs.code']}</dt>
          <dd>
            <span className="code-chip">{s.code}</span>
          </dd>
        </div>
        <div>
          <dt>{t['hs.type']}</dt>
          <dd>
            <TypePill school={s} />
          </dd>
        </div>
        <div>
          <dt>{t['hs.state']}</dt>
          <dd>
            <StatePill school={s} />
          </dd>
        </div>
        <div>
          <dt>{t['hs.paperwork']}</dt>
          <dd>
            <PaperworkPill school={s} />
          </dd>
        </div>
      </dl>
      {home.classes_missing_size > 0 && <p className="notice">{t['hs.missingSizes'](home.classes_missing_size)}</p>}
      {facts?.classes_without_teacher && facts.classes_without_teacher.length > 0 && (
        <p className="notice">{t['r4.noTeacher'](facts.classes_without_teacher.join(', '))}</p>
      )}
      <h3 className="field-label">{t['pa.checklist']}</h3>
      <ul className="checklist">
        {home.checklist.map((c) => (
          <li key={c.key} className={c.done ? 'done' : c.level === 'required' ? 'todo-req' : 'todo-warn'}>
            <span className="ck-mark" aria-hidden="true">
              {c.done ? '✓' : c.level === 'required' ? '!' : '○'}
            </span>
            <span className="ck-text">
              {checklistLabel(t, c.key, sandbox)}
              {!c.done && c.missing && c.missing.length > 0 && <span className="ck-detail"> · {t['r4.ready.missingLangs'](langList(t, c.missing))}</span>}
              {!c.done && c.owner && <span className="ck-detail"> · {t['r4.owner'](ownerLabel(t, c.owner))}</span>}
              <span className="visually-hidden">: </span>
              <span className="ck-meta">
                {c.done ? (
                  <Pill kind="good">{t['pa.done']}</Pill>
                ) : c.level === 'required' ? (
                  <Pill kind="crit">{t['pa.requiredTodo']}</Pill>
                ) : (
                  <Pill kind="warn">{t['pa.warningTodo']}</Pill>
                )}
              </span>
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

// B9 (D3, D6): until the school admin has their own screens, the head asks
// Zencera support to add a deputy. At most once a day.
function AskForDeputy() {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await rpc('head_request_deputy_help', { p_note: note.trim() || null });
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    setSent(true);
    announce(t['r4.head.asked']);
  }

  if (sent) return <p className="notice notice-block" role="status">{t['r4.head.asked']}</p>;
  return (
    <div className="stack">
      <p className="text-muted">{t['r4.head.noDeputies']}</p>
      {!open ? (
        <p>
          <button type="button" className="btn btn-ghost" onClick={() => setOpen(true)}>{t['r4.head.askSupport']}</button>
        </p>
      ) : (
        <form className="form" onSubmit={send} noValidate>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-n`}>{t['r4.head.askNote']}</label>
            <input id={`${id}-n`} className="field" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          {error && <p className="error-text" role="alert">{error}</p>}
          <div className="form-actions">
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>{t['form.cancel']}</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{t['r4.head.askSend']}</button>
          </div>
        </form>
      )}
    </div>
  );
}

// ── Deputies, their scope, the approval delegate, the hand-over (primary only) ──

function Deputies({ home, reload }: { home: HeadHomeData; reload: () => Promise<void> }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const deputies = home.deputies ?? [];
  const [scopeFor, setScopeFor] = useState<Deputy | null>(null);
  const [delegateFor, setDelegateFor] = useState<Deputy | null>(null);
  const [primaryFor, setPrimaryFor] = useState<Deputy | null>(null);
  const [endOpen, setEndOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run(fn: string, args: Record<string, unknown>, done: string, close: () => void) {
    setBusy(true);
    setError(null);
    const r = await rpc(fn, args);
    setBusy(false);
    if (r.error) return setError(errorText(t, r.error));
    announce(done);
    close();
    await reload();
  }
  const closeAll = () => {
    setScopeFor(null);
    setDelegateFor(null);
    setPrimaryFor(null);
    setEndOpen(false);
    setError(null);
  };

  const d = home.delegate;

  return (
    <section className="panel p5" aria-labelledby="hs-deps">
      <h2 className="panel-title" id="hs-deps">
        {t['hs.deputiesTitle']}
      </h2>
      <p className="form-hint">{t['hs.deputiesIntro']}</p>

      <div className="notice-block" aria-live="polite">
        <strong>{t['hs.delegateTitleBox']}: </strong>
        {d ? (d.ends_on ? t['hs.delegateCurrentUntil'](d.name, fmtDate(locale, d.ends_on)) : t['hs.delegateCurrent'](d.name)) : t['hs.delegateNone']}{' '}
        {d && (
          <button type="button" className="link-btn" onClick={() => setEndOpen(true)}>
            {t['hs.endDelegate']}
          </button>
        )}
      </div>

      {deputies.length === 0 ? (
        <AskForDeputy />
      ) : (
        <ul className="people-list">
          {deputies.map((p) => (
            <li key={p.user_id} className="person-row">
              <span className="person-main">
                <span className="person-name">{p.name}</span>
                {p.is_test && <span className="test-badge">{t['ctx.testBadge']}</span>}
                {p.is_delegate && (
                  <Pill kind="good">
                    {d?.ends_on ? t['hs.delegatePillUntil'](fmtDate(locale, d.ends_on)) : t['hs.delegatePill']}
                  </Pill>
                )}
              </span>
              <span className="person-meta">
                {p.scope_grades ? t['hs.scopeGradesShort'](p.scope_grades.join(', ')) : t['hs.scopeWholeShort']}
              </span>
              <span className="person-meta row-links">
                <button type="button" className="link-btn" onClick={() => setScopeFor(p)}>
                  {t['hs.changeScope']}
                </button>
                {!p.is_delegate && (
                  <button type="button" className="link-btn" onClick={() => setDelegateFor(p)}>
                    {t['hs.makeDelegate']}
                  </button>
                )}
                <button type="button" className="link-btn" onClick={() => setPrimaryFor(p)}>
                  {t['hs.makePrimary']}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <ScopeDialog
        target={scopeFor}
        grades={home.grades ?? []}
        busy={busy}
        error={error}
        onClose={closeAll}
        onSave={(ids) =>
          scopeFor &&
          run('head_set_deputy_scope', { p_user_id: scopeFor.user_id, p_grade_ids: ids }, t['hs.scopeSaved'](scopeFor.name), closeAll)
        }
      />
      <DelegateDialog
        target={delegateFor}
        current={d?.name ?? null}
        busy={busy}
        error={error}
        onClose={closeAll}
        onSave={(endsOn) =>
          delegateFor &&
          run('head_set_delegate', { p_user_id: delegateFor.user_id, p_ends_on: endsOn }, t['hs.delegateSaved'](delegateFor.name), closeAll)
        }
      />
      <HandOverDialog
        target={primaryFor}
        busy={busy}
        error={error}
        onClose={closeAll}
        onSave={(note) =>
          primaryFor &&
          run('head_hand_over_primary', { p_user_id: primaryFor.user_id, p_note: note }, t['hs.handedOver'](primaryFor.name), closeAll)
        }
      />
      <Dialog
        open={endOpen}
        title={t['hs.endDelegateTitle']}
        intro={d && <p className="form-hint">{t['hs.endDelegateBody'](d.name)}</p>}
        submitLabel={t['hs.endDelegate']}
        danger
        busy={busy}
        error={error}
        onClose={closeAll}
        onSubmit={() => run('head_end_delegate', {}, t['hs.delegateEnded'], closeAll)}
      />
    </section>
  );
}

function ScopeDialog({
  target,
  grades,
  busy,
  error,
  onClose,
  onSave,
}: {
  target: Deputy | null;
  grades: { id: string; label: string }[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (ids: string[] | null) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const [mode, setMode] = useState<'whole' | 'grades'>('whole');
  const [ids, setIds] = useState<string[]>([]);

  useEffect(() => {
    if (!target) return;
    setMode(target.scope_grade_ids ? 'grades' : 'whole');
    setIds(target.scope_grade_ids ?? []);
  }, [target]);

  const invalid = mode === 'grades' && ids.length === 0;

  return (
    <Dialog
      open={!!target}
      title={t['hs.scopeTitle'](target?.name ?? '')}
      intro={<p className="form-hint">{t['hs.scopeWhy']}</p>}
      submitLabel={t['pa.save']}
      busy={busy}
      error={error}
      submitDisabled={invalid}
      onClose={onClose}
      onSubmit={() => onSave(mode === 'whole' ? null : ids)}
    >
      <fieldset className="choice-list">
        <legend className="visually-hidden">{t['hs.scopeTitle'](target?.name ?? '')}</legend>
        <label className={`choice${mode === 'whole' ? ' on' : ''}`}>
          <span className="choice-head">
            <input type="radio" name={`${id}-m`} checked={mode === 'whole'} onChange={() => setMode('whole')} />
            <span>
              <strong>{t['hs.scopeWholeOption']}</strong>
              <span className="choice-desc">{t['hs.scopeWholeHint']}</span>
            </span>
          </span>
        </label>
        <label className={`choice${mode === 'grades' ? ' on' : ''}`}>
          <span className="choice-head">
            <input
              type="radio"
              name={`${id}-m`}
              checked={mode === 'grades'}
              disabled={grades.length === 0}
              onChange={() => setMode('grades')}
            />
            <span>
              <strong>{t['hs.scopeGradesOption']}</strong>
              <span className="choice-desc">{t['hs.scopeGradesHint']}</span>
            </span>
          </span>
        </label>
      </fieldset>
      {grades.length === 0 && <p className="form-hint">{t['hs.scopeNoGrades']}</p>}
      {mode === 'grades' && grades.length > 0 && (
        <fieldset className="field-group" aria-describedby={invalid ? `${id}-err` : undefined}>
          <legend className="field-label">{t['hs.scopeGradesLegend']}</legend>
          <span className="level-boxes">
            {grades.map((g) => (
              <label key={g.id} className={`chip-check${ids.includes(g.id) ? ' on' : ''}`}>
                <input
                  type="checkbox"
                  checked={ids.includes(g.id)}
                  onChange={(e) => setIds(e.target.checked ? [...ids, g.id] : ids.filter((x) => x !== g.id))}
                />
                {t['hs.gradeN'](g.label)}
              </label>
            ))}
          </span>
          {invalid && (
            <p className="form-hint" id={`${id}-err`}>
              {t['hs.scopePickOne']}
            </p>
          )}
        </fieldset>
      )}
    </Dialog>
  );
}

// Dates are the school's local calendar days; "today" is the browser's.
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function plusDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function DelegateDialog({
  target,
  current,
  busy,
  error,
  onClose,
  onSave,
}: {
  target: Deputy | null;
  current: string | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (endsOn: string | null) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const [until, setUntil] = useState('');
  useEffect(() => {
    if (target) setUntil('');
  }, [target]);
  return (
    <Dialog
      open={!!target}
      title={t['hs.delegateTitle'](target?.name ?? '')}
      intro={
        <>
          <p className="form-hint">{t['hs.delegateIntro']}</p>
          {current && <p className="notice">{t['hs.delegateReplaces'](current)}</p>}
        </>
      }
      submitLabel={t['hs.makeDelegate']}
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() => onSave(until || null)}
    >
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-u`}>
          {t['hs.delegateUntil']}
        </label>
        <input
          id={`${id}-u`}
          className="field"
          type="date"
          min={todayIso()}
          max={plusDaysIso(366)}
          value={until}
          onChange={(e) => setUntil(e.target.value)}
          aria-describedby={`${id}-uh`}
        />
        <p className="form-hint" id={`${id}-uh`}>
          {t['hs.delegateUntilHint']}
        </p>
      </div>
    </Dialog>
  );
}

function HandOverDialog({
  target,
  busy,
  error,
  onClose,
  onSave,
}: {
  target: Deputy | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (note: string | null) => void;
}) {
  const { t } = useI18n();
  const id = useId();
  const [note, setNote] = useState('');
  useEffect(() => {
    if (target) setNote('');
  }, [target]);
  return (
    <Dialog
      open={!!target}
      title={t['hs.handOverTitle'](target?.name ?? '')}
      intro={<p className="form-hint">{t['hs.handOverBody']}</p>}
      submitLabel={t['hs.makePrimary']}
      danger
      busy={busy}
      error={error}
      onClose={onClose}
      onSubmit={() => onSave(note.trim() || null)}
    >
      <div className="field-group">
        <label className="field-label" htmlFor={`${id}-n`}>
          {t['hs.handOverNote']}
        </label>
        <input
          id={`${id}-n`}
          className="field"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-describedby={`${id}-nh`}
        />
        <p className="form-hint" id={`${id}-nh`}>
          {t['hs.handOverNoteHint']}
        </p>
      </div>
    </Dialog>
  );
}
